import makeStyles from '@mui/styles/makeStyles';
import Tooltip from "@mui/material/Tooltip";
import {selfQuizAnswered} from "business/quiz";
import {isMentor} from "business/role";

import {color, type} from "common/tokens";
import CtaButton from "components/redesign/CtaButton";
import Dialog from "components/redesign/Dialog";
import {Check} from "lucide-react";
import QuizAnswerWrapper from "components/QuizAnswers/QuizAnswerWrapper";
import MentorTips from "components/SessionStepRenderer/MentorTips";
import StepImage from "components/SessionStepRenderer/StepImage";
import StepLink from "components/SessionStepRenderer/StepLink";
import StepText from "components/SessionStepRenderer/StepText";
import Video from "components/Video";
import {flatten, fromPairs, get, noop} from "lodash";
import React, {useEffect, useRef, useState} from 'react';
import MentorshipQuizAnswerWrapper from "components/QuizAnswers/MentorshipQuizAnswerWrapper";
import {useTranslation} from "react-i18next";
import { useSetTranslation } from "common/useSetTranslation";

const useStyles = makeStyles({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: 15.3,
    // Generous at the foot so the last line of a step clears the footer
    // instead of ending against it.
    padding: '15px 0 64px',
  },
  challenge: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
  },
  challengeNote: {
    ...type.body,
    color: color.TEXT,
    margin: 0,
  },
  answers: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 16,
    width: '100%',
  },
});

const quizHasMandatoryQuestions = (quiz) =>
  flatten(quiz.questions).some(q => q.mandatory === true);

const QuizQuestionAndAnswer = ({
  quiz,
  stepId,
  sessionId,
  quizAnswers,
  quizRelevancy,
  initQuiz,
  initQuizAnswers = noop(),
  role,
  partnerDetails,
  isLearningHub,
  allowRetake = true,
  helpText,
  isPreview,
}) => {
  useEffect(() => {
    if (!isPreview) {
      initQuizAnswers(sessionId, stepId, quiz.id, isLearningHub);
    }
  }, []);
  const {t} = useTranslation();

  const classes = useStyles();
  const [showAnswers, setShowAnswers] = useState(false);
  const isQuizComplete = selfQuizAnswered(role, quizAnswers, isLearningHub);
  const isMandatory = quizHasMandatoryQuestions(quiz);
  // A mandatory challenge holds its answers back — the reader's own and the
  // group's — until it has been taken. An optional one is open either way.
  const canViewAnswers = isLearningHub ? isQuizComplete : (!isMandatory || isQuizComplete);

  let buttonTitle = ''
  if (isQuizComplete) {
    buttonTitle = isMandatory ? t('quizTakenMandatory') : t('quizTakenOptional')
  } else {
    buttonTitle = isMandatory ? t('takeQuizMandatory') : t('takeQuizOptional')
  }

  const handleQuizClick = () => {
    if (isPreview) return;
    initQuiz(quiz.id, stepId, isLearningHub);
  };

  const answers = isLearningHub ? (
    <QuizAnswerWrapper
      quizQuestions={quiz.questions}
      quizAnswers={quizAnswers}
      helpText={helpText}
      role={role}
      isLearningHub={isLearningHub}
      partnerDetails={partnerDetails}
    />
  ) : (
    <MentorshipQuizAnswerWrapper
      quizQuestions={quiz.questions}
      quizAnswers={quizAnswers}
      quizRelevancy={quizRelevancy}
      helpText={helpText}
      role={role}
      isLearningHub={isLearningHub}
      partnerDetails={partnerDetails}
    />
  );

  const challengeButton = (
    <CtaButton
      size={'large'}
      variant={'challenge'}
      required={isMandatory}
      dimmed={isQuizComplete}
      disabled={isQuizComplete && !allowRetake}
      icon={isQuizComplete ? <Check size={18} strokeWidth={2.5}/> : undefined}
      label={buttonTitle}
      onClick={handleQuizClick}
    />
  );

  return (
    <div className={classes.challenge}>
      {isPreview ? (
        <Tooltip title="You are in preview mode" placement="top" arrow>
          <span>{challengeButton}</span>
        </Tooltip>
      ) : challengeButton}

      {isQuizComplete && (
        <p className={classes.challengeNote}>
          {allowRetake ? t('retakeAnytimeOrInCall') : t('retakeQuizNotAllowed')}
        </p>
      )}

      {/* Answers stay folded away until asked for, so a taken challenge does
          not bury the rest of the step. */}
      {canViewAnswers && (
        <CtaButton
          size={'large'}
          variant={'challenge'}
          required={isMandatory}
          onClick={() => setShowAnswers(true)}
          label={t('viewAnswer')}
        />
      )}

      {showAnswers && (
        <Dialog onClose={() => setShowAnswers(false)} title={t('viewAnswer')} maxWidth={880}>
          {answers}
        </Dialog>
      )}
    </div>
  );
};

const SessionStep = ({
  step,
  sessionId,
  role,
  initQuiz,
  initQuizAnswers,
  quizAnswers,
  isLearningHub,
  partnerDetails,
  isInView,
  isPreview,
  onContentResize = noop,
}) => {
  const classes = useStyles();
  const { st } = useSetTranslation();
  const bodyRef = useRef(null);

  // The container measures this step once and then clips it, so everything that
  // grows afterwards has to ask for a fresh measurement or its tail is lost.
  // An image is the usual culprit: it contributes no height until it decodes.
  // ResizeObserver alone is not enough — it is missing in some webviews — so
  // the image load events and a short settling schedule carry the guarantee.
  useEffect(() => {
    const body = bodyRef.current;
    if (!isInView || !body) return undefined;

    let cancelled = false;
    const sync = () => {
      if (!cancelled) onContentResize();
    };

    const images = Array.from(body.querySelectorAll('img')).filter(img => !img.complete);
    images.forEach(img => {
      img.addEventListener('load', sync);
      img.addEventListener('error', sync);
    });

    // Covers what no event announces — fonts swapping, markdown reflowing, a
    // player taking its size — without leaving a timer running for the session.
    const timers = [0, 150, 400, 1000, 2200].map(delay => setTimeout(sync, delay));

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(sync);
    if (observer) observer.observe(body);
    window.addEventListener('resize', sync);

    sync();

    return () => {
      cancelled = true;
      timers.forEach(clearTimeout);
      images.forEach(img => {
        img.removeEventListener('load', sync);
        img.removeEventListener('error', sync);
      });
      if (observer) observer.disconnect();
      window.removeEventListener('resize', sync);
    };
    // quizAnswers is a dependency because answers landing from the API grow the
    // step well after it was first measured.
  }, [isInView, onContentResize, quizAnswers, step]);

  const getAnswersForQuiz = (qa, sId, stId, qId) => {
    return get(qa, `[${sId}][${stId}][${qId}].answers`, []);
  };

  const stepItemRender = (item, stepId, isSingleStepContent) => {
    const stepItems = fromPairs([
      ['TEXT', () => (<StepText text={item.text} localeText={item.localeText} />)],
      ['INSTRUCTION', () => (<StepText text={item.text} localeText={item.localeText} />)],
      ['MENTOR_TIPS', () => isMentor(role) ? <MentorTips text={item.text} localeText={item.localeText} /> : null],
      ['ARTICLE', () => (<StepText text={item.text} localeText={item.localeText} />)],
      ['IMAGE', () => <StepImage img={item.image} localeImg={get(item, 'localeImage')} />],
      ['YOUTUBE', () => (
        <Video
          videoId={st(item.url, item.localeUrl).split('v=')[1]}
          autoPlay={isInView && isSingleStepContent}
        />
      )],
      ['HYPERLINK', () => <StepLink url={item.url} localeUrl={item.localeUrl} />],
      ['QUIZ', () => (
        <QuizQuestionAndAnswer
          quiz={item.quiz}
          stepId={stepId}
          sessionId={sessionId}
          helpText={st(item.quiz.helpText, item.quiz.localeHelpText)}
          initQuiz={initQuiz}
          role={role}
          isLearningHub={isLearningHub}
          quizAnswers={getAnswersForQuiz(quizAnswers, sessionId, stepId, item.quiz.id)}
          quizRelevancy={item.quizRelevancy}
          initQuizAnswers={initQuizAnswers}
          partnerDetails={partnerDetails}
          allowRetake={item.allowRetake}
          isPreview={isPreview}
        />
      )],
    ]);

    return get(stepItems, item.contentType, noop);
  };

  return (
    <div className={classes.body} ref={bodyRef}>
      {step.content.map((item) => (
        <div key={item.id}>
          {stepItemRender(item, step.id, step.content.length === 1)()}
        </div>
      ))}
    </div>
  );
};

export default SessionStep;
